import React, { useState } from 'react';
import SectionHeading from '../ui/SectionHeading';
import styles from './PerceptionLab.module.css';

/*
 * Interactive companion to my current robot-learning study: a vision-only
 * behavior-cloning policy is trained on PickPlaceCan from one fixed camera,
 * then evaluated as the camera drifts away from that training viewpoint.
 * The numbers here are illustrative curves shaped like the real experiment,
 * not live rollout telemetry.
 */

const MAX_OFFSET = 40;

/* Success falls off as a gaussian in offset: the fixed-camera policy has a
   narrow competence peak, camera-randomized training widens it. */
const successAt = (offset, peak, width) =>
  Math.round(peak * Math.exp(-((offset / width) ** 2)));

const POLICIES = [
  { id: 'fixed', label: 'Fixed-camera BC', peak: 92, width: 12 },
  { id: 'randomized', label: '+ Camera randomization', peak: 84, width: 30 },
];

/* Plot geometry: offset 0-40° maps to x, success 0-100% maps to y. */
const plotX = (offset) => 46 + offset * 9.5;
const plotY = (success) => 235 - success * 1.85;

function curvePath(policy) {
  const points = [];
  for (let offset = 0; offset <= MAX_OFFSET; offset += 2) {
    points.push(
      `${points.length ? 'L' : 'M'}${plotX(offset)} ${plotY(
        successAt(offset, policy.peak, policy.width)
      )}`
    );
  }
  return points.join(' ');
}

function policyState(success) {
  if (success >= 50) return 'robust';
  if (success >= 20) return 'degrading';
  return 'failing';
}

export default function PerceptionLab() {
  const [offset, setOffset] = useState(8);

  const readings = POLICIES.map((policy) => ({
    ...policy,
    success: successAt(offset, policy.peak, policy.width),
  }));

  const guidance =
    offset <= 10
      ? 'Both are competent near train view; supervised loss does not expose the gap.'
      : offset <= 25
        ? 'Fixed-camera collapse exposes the data distribution gap; randomization still holds.'
        : 'Both policies fail here. Randomization widens, but does not remove, the competence range.';

  // Camera glyph rides an arc over the workspace as the offset grows.
  const camAngle = ((offset * 1.9 - 90) * Math.PI) / 180;
  const camX = 152 + Math.cos(camAngle) * 88;
  const camY = 218 + Math.sin(camAngle) * 88;

  return (
    <section id="lab" className={styles.section} aria-labelledby="lab-title">
      <SectionHeading
        id="lab-title"
        description="A hands-on cut of my current robot-learning study: train a vision-only manipulation policy from one camera, then shift the viewpoint and watch success collapse. Drag the offset to trace the degradation curve — synthetic numbers, real phenomenon."
      >
        Research you can poke at
      </SectionHeading>

      <figure className={styles.instrument} aria-labelledby="perception-experiment-title">
        <div className={styles.instrumentHeader}>
          <div className={styles.instrumentTitle}>
            <span className={styles.kicker}>Perception lab / study 01</span>
            <h3 id="perception-experiment-title">Viewpoint robustness</h3>
          </div>
          <p className={styles.metadata}>PickPlaceCan · Panda · RGB only</p>
          <div className={styles.offsetReadout}>
            <span>Live offset</span>
            <output aria-label="Current camera offset">{offset}°</output>
          </div>
        </div>

        <div className={styles.viewport}>
          <p className={styles.disclosure}>
            Illustrative curves — not live rollout telemetry.
          </p>
          <div className={styles.visualGrid}>
            <svg
              className={styles.sceneSvg}
              viewBox="0 0 320 280"
              role="img"
              aria-labelledby="lab-viz-title lab-viz-desc"
            >
              <title id="lab-viz-title">Viewpoint robustness explorer</title>
              <desc id="lab-viz-desc">
                A camera orbits away from its training position over a robot workspace while a
                chart plots policy success against camera offset.
              </desc>

              <text className={styles.plotHeading} x="18" y="24">
                Robot workspace
              </text>
              <text className={styles.plotIndex} x="302" y="24" textAnchor="end">
                A
              </text>

              {/* Panda-style arm, can, target bin, and camera arc. */}
              <g className={styles.scene}>
                <path className={styles.ground} d="M18 235h284" />
                <rect className={styles.armBase} x="38" y="211" width="42" height="24" rx="5" />
                <path className={styles.arm} d="M59 211 88 153l52 28" />
                <circle className={styles.joint} cx="59" cy="211" r="6" />
                <circle className={styles.joint} cx="88" cy="153" r="6" />
                <path className={styles.gripper} d="M140 181l14 8m-14-8 2 16" />
                <rect className={styles.can} x="174" y="207" width="18" height="26" rx="3" />
                <path className={styles.bin} d="M232 235v-21h42v21" />

                <path className={styles.camArc} d="M152 130a88 88 0 0 1 85 66" />
                <circle className={styles.camHome} cx="152" cy="130" r="4" />
                <text className={styles.sceneLabel} x="139" y="114">
                  train view
                </text>
                <line
                  className={styles.sightLine}
                  x1={camX}
                  y1={camY}
                  x2="183"
                  y2="207"
                />
                <g className={styles.camera} transform={`translate(${camX} ${camY})`}>
                  <rect x="-13" y="-9" width="20" height="18" rx="4" />
                  <path d="M7 -4 16 -9v18L7 4" />
                </g>
              </g>
              <text className={styles.baselineLabel} x="18" y="261">
                Manipulation scene
              </text>
              <text className={styles.baselineLabel} x="302" y="261" textAnchor="end">
                current camera pose
              </text>
            </svg>

            <svg
              className={styles.chartSvg}
              viewBox="0 0 460 280"
              role="img"
              aria-labelledby="lab-chart-title lab-chart-desc"
            >
              <title id="lab-chart-title">Policy degradation chart</title>
              <desc id="lab-chart-desc">
                Fixed-camera and camera-randomized policy success rates across zero to forty
                degrees of camera offset.
              </desc>

              <text className={styles.plotHeading} x="46" y="24">
                Success rate
              </text>
              <text className={styles.plotIndex} x="426" y="24" textAnchor="end">
                B
              </text>

              {[0, 50, 100].map((tick) => (
                <g key={tick}>
                  <line
                    className={styles.gridLine}
                    x1="46"
                    y1={plotY(tick)}
                    x2="426"
                    y2={plotY(tick)}
                  />
                  <text
                    className={styles.tick}
                    x="36"
                    y={plotY(tick) + 4}
                    textAnchor="end"
                  >
                    {tick}%
                  </text>
                </g>
              ))}
              {[0, 20, 40].map((tick) => (
                <text
                  className={styles.tick}
                  key={tick}
                  x={plotX(tick)}
                  y="257"
                  textAnchor={tick === 0 ? 'start' : tick === 40 ? 'end' : 'middle'}
                >
                  {tick}°
                </text>
              ))}
              <path className={styles.axis} d="M46 50v185h380" />
              <line
                className={styles.offsetLine}
                x1={plotX(offset)}
                y1="50"
                x2={plotX(offset)}
                y2="235"
              />

              {readings.map((policy) => (
                <g key={policy.id} data-policy={policy.id}>
                  <path
                    className={
                      policy.id === 'fixed' ? styles.curveFixed : styles.curveRandomized
                    }
                    d={curvePath(policy)}
                  />
                  {policy.id === 'fixed' ? (
                    <circle
                      className={styles.marker}
                      cx={plotX(offset)}
                      cy={plotY(policy.success)}
                      r="5.5"
                    />
                  ) : (
                    <rect
                      className={styles.marker}
                      x={plotX(offset) - 4.5}
                      y={plotY(policy.success) - 4.5}
                      width="9"
                      height="9"
                      transform={`rotate(45 ${plotX(offset)} ${plotY(policy.success)})`}
                    />
                  )}
                </g>
              ))}
              <text className={styles.axisLabel} x="426" y="274" textAnchor="end">
                camera offset
              </text>
            </svg>
          </div>
        </div>

        <div className={styles.dock}>
          <div className={styles.tuner}>
            <div className={styles.tunerHead}>
              <label htmlFor="camera-offset">
                <span>Control 01</span>
                Camera offset
              </label>
              <span className={styles.range}>0—40°</span>
            </div>
            <input
              id="camera-offset"
              type="range"
              min="0"
              max={MAX_OFFSET}
              step="1"
              value={offset}
              aria-valuetext={`${offset} degrees`}
              onChange={(event) => setOffset(Number(event.target.value))}
            />
            <p className={styles.interpretation} role="status" aria-live="polite">
              At {offset}°: fixed {readings[0].success}%, randomized {readings[1].success}%.{' '}
              {guidance}
            </p>
          </div>

          <ul className={styles.chips} aria-label="Policy variants">
            {readings.map((policy) => (
              <li key={policy.id} data-state={policyState(policy.success)}>
                <span
                  className={styles.chipSwatch}
                  data-policy={policy.id}
                  aria-hidden="true"
                />
                <span className={styles.chipLabel}>{policy.label}</span>
                <span className={styles.chipState}>{policyState(policy.success)}</span>
                <strong className={styles.chipValue}>{policy.success}%</strong>
              </li>
            ))}
          </ul>
        </div>
      </figure>
    </section>
  );
}
